import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Radio, Spin } from "antd";
import { ResponsiveLine } from "@nivo/line";
import { dineroDisabledMask } from "../../../Components/Items";

// Mismo algoritmo de bucketing que ReporteVentas.js (crearGrafico): día si el
// rango cabe en <=31 días, semana si <180, mes si <36 meses, año si no.
// Ahí lo hacen adentro del componente leyendo fechas en formato string; acá es
// genérico -- recibe filas ya con Date reales y el campo a sumar por catálogo.
function bucketizar(filas, campoFechaKey, campoValorKey, fechaInicial, fechaFinal) {
  if (!fechaInicial || !fechaFinal) return [];

  const fechaFinalMoment = moment(fechaFinal);
  let unidad = "day";
  let formato = "DD/MM/YYYY";
  const diasTotales = fechaFinalMoment.diff(moment(fechaInicial), "days");
  if (diasTotales <= 31) {
    unidad = "day";
    formato = "DD/MM/YYYY";
  } else if (diasTotales < 180) {
    unidad = "week";
    formato = "DD/MM/YYYY";
  } else if (fechaFinalMoment.diff(moment(fechaInicial), "months") < 36) {
    unidad = "month";
    formato = "MMM YYYY";
  } else {
    unidad = "year";
    formato = "YYYY";
  }

  const puntos = [];
  let cursor = moment(fechaInicial);
  let acumulado = 0;
  let guardarraíl = 0;
  while (!cursor.isAfter(fechaFinalMoment, unidad) && guardarraíl < 400) {
    guardarraíl += 1;
    // eslint-disable-next-line no-loop-func
    const delPeriodo = filas.filter((f) => moment(f[campoFechaKey]).isSame(cursor, unidad));
    const suma = delPeriodo.reduce((acc, f) => acc + (Number(f[campoValorKey]) || 0), 0);
    acumulado += suma;
    puntos.push({ x: cursor.format(formato), y: suma, yAcumulado: acumulado });
    cursor = cursor.clone().add(1, unidad);
  }
  return puntos;
}

function GraficaReporte({ empresaId, headersToken, modeloKey, camposNumericos, filtros, rango, plantillaId }) {
  const [campoValor, setCampoValor] = useState(camposNumericos[0]?.key);
  const [filas, setFilas] = useState([]);
  const [meta, setMeta] = useState({ campoFechaKey: null, campoValorLabel: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (camposNumericos.length > 0 && !camposNumericos.find((c) => c.key === campoValor)) {
      setCampoValor(camposNumericos[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camposNumericos]);

  useEffect(() => {
    if (!campoValor) return;
    setLoading(true);
    axios
      .post(
        "creadorDeReportes/grafica",
        { empresaId, modeloKey, campoValor, filtros, plantillaId },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return;
        setFilas(res.data.filas || []);
        setMeta({ campoFechaKey: res.data.campoFechaKey, campoValorLabel: res.data.campoValorLabel });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, modeloKey, campoValor, JSON.stringify(filtros), plantillaId]);

  const puntos = useMemo(
    () => bucketizar(filas, meta.campoFechaKey, campoValor, rango?.[0], rango?.[1]),
    [filas, meta.campoFechaKey, campoValor, rango]
  );

  const dataGrafica = [
    { id: meta.campoValorLabel || "Valor", color: "#2563EB", data: puntos.map((p) => ({ x: p.x, y: p.y })) },
    {
      id: `${meta.campoValorLabel || "Valor"} Acumulado`,
      color: "#93C5FD",
      data: puntos.map((p) => ({ x: p.x, y: p.yAcumulado })),
    },
  ];

  if (camposNumericos.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px 0", color: "#8c8c8c", fontSize: 13 }}>
        Elige al menos un campo numérico o de moneda (que no sea "solo detalle") para poder graficarlo.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "#595959" }}>Graficar:</span>
        <Radio.Group size="small" value={campoValor} onChange={(e) => setCampoValor(e.target.value)}>
          {camposNumericos.map((c) => (
            <Radio.Button key={c.key} value={c.key}>
              {c.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
      <Spin spinning={loading}>
        <div style={{ height: 320 }}>
          <ResponsiveLine
            data={dataGrafica}
            margin={{ top: 15, right: 20, bottom: 70, left: 80 }}
            enableSlices="x"
            useMesh
            yFormat={(e) => dineroDisabledMask(Math.abs(e))}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: 0, max: "auto", stacked: false, reverse: false }}
            curve="monotoneX"
            lineWidth={2}
            pointSize={5}
            pointBorderWidth={1}
            pointColor={{ theme: "background" }}
            pointBorderColor={{ from: "serieColor" }}
            colors={["#2563EB", "#93C5FD"]}
            colorBy="index"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 8,
              tickRotation: 45,
              tickValues: (dataGrafica[0]?.data || []).map((d) => d.x).filter((_, i) => i % 2 === 0),
            }}
            axisLeft={{ tickSize: 5, tickPadding: 8, format: (v) => dineroDisabledMask(Math.abs(v)) }}
            enableGridX={false}
            legends={[
              {
                anchor: "top",
                direction: "row",
                translateY: -15,
                itemWidth: 160,
                itemHeight: 18,
                symbolSize: 10,
                symbolShape: "circle",
              },
            ]}
          />
        </div>
      </Spin>
    </div>
  );
}

export default GraficaReporte;
